import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { handleApiError } from "@utils/errorHandler";

import {
  TABLE_TRANSPORT_LIST,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
  FILTER_TYPE_BUTTON,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import Action from "./components/Action/Action";
import Country from "./components/Country/Country";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import { columnHeader, isEmpty } from "@utils/utils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewTransport,
    countries,
  } = props;

  const [status, setStatus] = useState(2);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);

  const refreshData = (data) => {
    setRefreshState(data);
  };
  let columns = [];

  columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="id" />,
    },
    {
      Header: t("COUNTRY"),
      title: "COUNTRY",
      accessor: "country",
      Cell: ({ cell: { value } }) => <Country data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("COUNTRY")}
          placeholder={t("SEARCH")}
          name="country"
          labelClass="products-list__country-label"
          containerClass="products-list__country-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={countries}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("TRANSPORT_TYPE_NAME"),
      title: "TRANSPORT_TYPE_NAME",
      accessor: "name",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="name" />,
    },
    {
      Header: t("TRANSPORT_TYPE_VOLUME"),
      title: "TRANSPORT_TYPE_VOLUME",
      accessor: "volume",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="volume" />
      ),
    },
    {
      Header: t("TRANSPORT_TYPE_AVG_WEIGHT"),
      title: "TRANSPORT_TYPE_AVG_WEIGHT",
      accessor: "averageWeight",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="averageWeight" />
      ),
    },
    {
      Header: t("TRANSPORT_TYPE_MIN_WEIGHT"),
      title: "TRANSPORT_TYPE_MIN_WEIGHT",
      accessor: "minWeight",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="minWeight" />
      ),
    },
    {
      Header: t("TRANSPORT_TYPE_MAX_WEIGHT"),
      title: "TRANSPORT_TYPE_MAX_WEIGHT",
      accessor: "maxWeight",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="maxWeight" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <Action
          data={value}
          column={column}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });
    // save request data,
    let country = [];
    if (appliedFilters !== undefined) {
      const countryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "country"
      );
      country =
        countryFilter?.value.length > 0
          ? countryFilter?.value
          : [1, 2, 3, 4, 5, 6, 7, 8];
    }
    // Set the loading state
    setLoading(true);
    axios
      .get(
        `/transports?countries=[${country}]&status=${status}&orderBy=${
          sort ? sort.dir : "desc"
        }&orderColumn=${
          sort ? sort.column : "id"
        }&page=${currentPage}&limit=${pageSize}`
      )
      .then((response) => {
        if (response?.data?.transports.length > 0) {
          const transportsList = response?.data?.transports;
          const responseData = transportsList.map((transport) => {
            const tempData = { ...transport };
            tempData.action = { ...tempData };
            tempData.volume = {
              volume: tempData.volume,
              status: tempData.isActive,
            };
            tempData.averageWeight = {
              averageWeight: tempData.averageWeight,
              status: tempData.isActive,
            };
            tempData.name = {
              name: tempData.name,
              status: tempData.isActive,
            };
            tempData.id = {
              id: tempData.id,
              status: tempData.isActive,
            };
            tempData.minWeight = {
              minWeight: tempData.minWeight,
              status: tempData.isActive,
            };
            tempData.maxWeight = {
              maxWeight: tempData.maxWeight,
              status: tempData.isActive,
            };

            tempData.country = {
              countryInfo: tempData.country,
              status: tempData.isActive,
            };
            return tempData;
          });
          setData([...responseData]);
          setPageCount(response?.data?.filteredRecords);
        } else {
          setData([]);
          setPageCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        handleApiError(error);
        setLoading(false);
      });
  };
  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    let statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        appliedFilters: requestData.appliedFilters,
      });
    }
  }, [status]);

  return (
    <EncloseTable
      type={TABLE_TRANSPORT_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={10}
      createNewAction={createNewTransport}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "TRANSPORT_TYPES_FILTER_ACTIVE_ONLY",
          name: "tranportactive",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
